import React, { useEffect, useState, useMemo } from 'react';
import { Container, Button, Form, Alert, Spinner, ListGroup, Badge, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import Layout from '../../../components/ui/Layout';
import { createLogger } from '../../../utils/logger';
import { buildInventoryPricingFields, serializeTypeDiscountDeals } from '../../../utils/inventoryPricing';

const logger = createLogger('CreateInventory');

const CreateInventory = () => {
  const { currentUser, userRole, employeeSupermarketId } = useAuth();
  const { showNotification } = useNotification();
  const [supermarkets, setSupermarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupermarketId, setSelectedSupermarketId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingInventory, setExistingInventory] = useState([]);
  
  // Discount deals state
  const [discountDeals, setDiscountDeals] = useState([]);
  const [dealQuantity, setDealQuantity] = useState('');
  const [dealPrice, setDealPrice] = useState('');
  const [typeSelections, setTypeSelections] = useState({});
  const [typePriceInputs, setTypePriceInputs] = useState({});
  const [typeQuantityInputs, setTypeQuantityInputs] = useState({});
  /** @type {Record<string, Array<{ id: number, quantity: number, price: number }>>} */
  const [typeDealsRecord, setTypeDealsRecord] = useState({});
  const [typeDealDrafts, setTypeDealDrafts] = useState({});
  
  const navigate = useNavigate();

  const productHasTypes = !!(selectedProduct?.product_types && selectedProduct.product_types.length > 0);

  const getFormBaselineUnitPrice = () => {
    const types = selectedProduct?.product_types || [];
    if (!types.length) {
      const sp = parseFloat(salePrice);
      return !Number.isNaN(sp) && sp > 0 ? sp : null;
    }
    const prices = [];
    types.forEach((t) => {
      if (t.id && typeSelections[t.id]) {
        const p = parseFloat(typePriceInputs[t.id]);
        if (!Number.isNaN(p) && p > 0) prices.push(p);
      }
    });
    return prices.length ? Math.min(...prices) : null;
  };

  const unitPriceForType = (typeId) => {
    const p = parseFloat(typePriceInputs[typeId]);
    return !Number.isNaN(p) && p > 0 ? p : null;
  };

  const isEmployeeLimited = userRole === 'Employee' && employeeSupermarketId;

  // Filter and paginate products based on search term
  const filteredProducts = useMemo(() => {
    let filteredList = products;

    // Apply search filter
    if (productSearchTerm.trim()) {
      filteredList = products.filter(product => 
        product.display_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(productSearchTerm.toLowerCase()))
      );
    }

    // Limit results for performance
    return filteredList.slice(0, 15);
  }, [products, productSearchTerm]);

  const handleProductSelect = (product) => {
    // Check if product is already in inventory
    if (checkIfProductAlreadyInInventory(product.id)) {
      showNotification({
        type: 'warning',
        title: 'Product Already in Inventory',
        message: 'This product has already been added to your supermarket\'s inventory. To update the quantity or price, please use the Edit Inventory option instead.'
      });
      setShowProductDropdown(false);
      return;
    }

    setSelectedProduct(product);
    setSelectedProductId(product.id);
    setProductSearchTerm(product.display_name);
    setShowProductDropdown(false);
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    setShowProductDropdown(true);
    if (!value.trim()) {
      setSelectedProduct(null);
      setSelectedProductId('');
    }
  };

  const checkIfProductAlreadyInInventory = (productId) => {
    if (!selectedSupermarketId) return false;
    return existingInventory.some(inventoryItem => 
      inventoryItem.supermarket_id === selectedSupermarketId && 
      inventoryItem.product_id === productId
    );
  };

  const addDiscountDeal = () => {
    if (!dealQuantity || !dealPrice) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please enter both quantity and price for the discount deal.'
      });
      return;
    }

    const quantityNum = parseInt(dealQuantity, 10);
    const priceNum = parseFloat(dealPrice);

    if (isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({
        type: 'warning',
        title: 'Invalid Quantity',
        message: 'Please enter a valid positive quantity.'
      });
      return;
    }

    if (isNaN(priceNum) || priceNum <= 0) {
      showNotification({
        type: 'warning',
        title: 'Invalid Price',
        message: 'Please enter a valid positive price.'
      });
      return;
    }

    if (discountDeals.some(deal => deal.quantity === quantityNum)) {
      showNotification({
        type: 'warning',
        title: 'Duplicate Deal',
        message: 'A discount deal with this quantity already exists.'
      });
      return;
    }

    const baselineUnit = getFormBaselineUnitPrice();
    if (baselineUnit == null) {
      showNotification({
        type: 'warning',
        title: 'Set prices first',
        message: 'Enter a valid sale price before adding a deal.',
      });
      return;
    }
    if (priceNum >= baselineUnit * quantityNum) {
      showNotification({
        type: 'warning',
        title: 'Invalid Deal Price',
        message: 'Deal price should be less than the regular price for the same quantity.'
      });
      return;
    }

    const newDeal = {
      id: Date.now(),
      quantity: quantityNum,
      price: priceNum
    };

    setDiscountDeals([...discountDeals, newDeal]);
    setDealQuantity('');
    setDealPrice('');
  };

  const removeDiscountDeal = (dealId) => {
    setDiscountDeals(discountDeals.filter(deal => deal.id !== dealId));
  };

  const getTypeDealDraft = (typeId) => typeDealDrafts[typeId] || { q: '', p: '' };

  const setTypeDealDraftField = (typeId, field, value) => {
    setTypeDealDrafts((prev) => ({
      ...prev,
      [typeId]: { q: '', p: '', ...prev[typeId], [field]: value },
    }));
  };

  const addDiscountDealForType = (typeId) => {
    const { q, p } = getTypeDealDraft(typeId);
    if (!q || !p) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Enter deal quantity and price for this type.',
      });
      return;
    }
    const quantityNum = parseInt(q, 10);
    const priceNum = parseFloat(p);
    if (Number.isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({ type: 'warning', title: 'Invalid Quantity', message: 'Enter a valid deal quantity.' });
      return;
    }
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      showNotification({ type: 'warning', title: 'Invalid Price', message: 'Enter a valid deal price.' });
      return;
    }
    const unit = unitPriceForType(typeId);
    if (unit == null) {
      showNotification({ type: 'warning', title: 'Set price first', message: 'Enter this type\'s unit price before adding deals.' });
      return;
    }
    const existing = typeDealsRecord[typeId] || [];
    if (existing.some((d) => d.quantity === quantityNum)) {
      showNotification({ type: 'warning', title: 'Duplicate Deal', message: 'A deal with this quantity already exists for this type.' });
      return;
    }
    if (priceNum >= unit * quantityNum) {
      showNotification({
        type: 'warning',
        title: 'Invalid Deal Price',
        message: 'Deal total should be less than the regular price for that many items.',
      });
      return;
    }
    const newDeal = { id: Date.now() + Math.random(), quantity: quantityNum, price: priceNum };
    setTypeDealsRecord((prev) => ({ ...prev, [typeId]: [...(prev[typeId] || []), newDeal] }));
    setTypeDealDrafts((prev) => ({ ...prev, [typeId]: { q: '', p: '' } }));
  };

  const removeDiscountDealForType = (typeId, dealId) => {
    setTypeDealsRecord((prev) => ({
      ...prev,
      [typeId]: (prev[typeId] || []).filter((d) => d.id !== dealId),
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        let supermarketsList = supermarketsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (isEmployeeLimited) {
          supermarketsList = supermarketsList.filter(s => s.id === employeeSupermarketId);
          if (supermarketsList.length > 0) {
            setSelectedSupermarketId(employeeSupermarketId);
          } else {
            setError("Your assigned supermarket could not be found. Please contact an admin.");
          }
        } else if (userRole === 'Employee' && !employeeSupermarketId) {
            showNotification({
              type: 'error',
              title: 'Access Denied',
              message: 'You are not assigned to a supermarket. Cannot add inventory.'
            });
        }
        setSupermarkets(supermarketsList);

        const productsSnapshot = await getDocs(collection(db, 'Products'));
        const productsList = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsList);

      } catch (err) {
        setError(`Failed to fetch initial data. ${  err.message}`);
        showNotification({
          type: 'error',
          title: 'Error',
          message: `Failed to fetch initial data. ${err.message}`
        });
        logger.error(err);
      }
      setPageLoading(false);
    };
    if (currentUser) {
        fetchData();
    }
    // Intentionally omit showNotification: avoid refetching initial data when notification context identity changes.
  }, [currentUser, userRole, employeeSupermarketId, isEmployeeLimited]);

  // Fetch existing inventory when supermarket changes
  useEffect(() => {
    const fetchExistingInventory = async () => {
      if (!selectedSupermarketId) {
        setExistingInventory([]);
        return;
      }

      try {
        const inventorySnapshot = await getDocs(collection(db, 'Inventory'));
        const inventoryList = inventorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Filter inventory for the selected supermarket
        const supermarketInventory = inventoryList.filter(item => 
          item.supermarket_id === selectedSupermarketId
        );
        
        setExistingInventory(supermarketInventory);
      } catch (err) {
        logger.error('Failed to fetch existing inventory:', err);
        setExistingInventory([]);
      }
    };

    fetchExistingInventory();
  }, [selectedSupermarketId]);

  useEffect(() => {
    if (!selectedProduct?.product_types?.length) {
      setTypeSelections({});
      setTypePriceInputs({});
      setTypeQuantityInputs({});
      setTypeDealsRecord({});
      setTypeDealDrafts({});
      return;
    }
    const sel = {};
    selectedProduct.product_types.forEach((t) => {
      if (t.id) sel[t.id] = false;
    });
    setTypeSelections(sel);
    setTypePriceInputs({});
    setTypeQuantityInputs({});
    setTypeDealsRecord({});
    setTypeDealDrafts({});
  }, [selectedProduct]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSupermarketId || !selectedProductId) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please fill in all required fields.'
      });
      return;
    }
    if (!productHasTypes && !quantity) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please enter quantity.'
      });
      return;
    }
    if (!productHasTypes && !salePrice) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please enter a sale price.'
      });
      return;
    }
    if (userRole === 'Employee' && !employeeSupermarketId) {
        showNotification({
          type: 'error',
          title: 'Access Denied',
          message: 'Cannot add inventory: You are not assigned to a supermarket.'
        });
        return;
    }
    if (isEmployeeLimited && selectedSupermarketId !== employeeSupermarketId) {
        showNotification({
          type: 'error',
          title: 'Access Denied',
          message: 'You can only add inventory for your assigned supermarket.'
        });
        return;
    }

    // Check if product is already in inventory
    if (checkIfProductAlreadyInInventory(selectedProductId)) {
        showNotification({
          type: 'warning',
          title: 'Product Already in Inventory',
          message: 'This product has already been added to your supermarket\'s inventory. To update the quantity or price, please use the Edit Inventory option instead.'
        });
        return;
    }

    const supermarket = supermarkets.find(s => s.id === selectedSupermarketId);
    const product = products.find(p => p.id === selectedProductId);

    if (!supermarket || !product) {
        showNotification({
          type: 'error',
          title: 'Invalid Selection',
          message: 'Selected supermarket or product details are invalid. Please refresh.'
        });
        return;
    }

    setLoading(true);
    try {
      let quantityNum;
      let type_quantities = {};
      let type_discount_deals = {};
      let salePriceNum;
      let type_prices = {};

      if (productHasTypes) {
        const rawPrices = {};
        selectedProduct.product_types.forEach((t) => {
          if (!t.id || !typeSelections[t.id]) return;
          const p = parseFloat(typePriceInputs[t.id]);
          if (Number.isNaN(p) || p <= 0) {
            throw new Error(`Enter a valid price for type "${t.name || t.id}".`);
          }
          rawPrices[t.id] = p;
          const q = parseInt(typeQuantityInputs[t.id], 10);
          if (Number.isNaN(q) || q < 0) {
            throw new Error(`Enter a valid quantity (0 or more) for type "${t.name || t.id}".`);
          }
          type_quantities[t.id] = q;
        });
        if (Object.keys(rawPrices).length === 0) {
          throw new Error('Select at least one product type and enter its price and quantity.');
        }
        const built = buildInventoryPricingFields(rawPrices, null);
        salePriceNum = built.sale_price;
        type_prices = built.type_prices;
        quantityNum = Object.values(type_quantities).reduce((a, b) => a + b, 0);
        type_discount_deals = serializeTypeDiscountDeals(typeDealsRecord);
      } else {
        quantityNum = parseInt(quantity, 10);
        if (Number.isNaN(quantityNum) || quantityNum <= 0) {
          throw new Error('Invalid quantity. Please enter a valid positive number.');
        }
        const salePriceNumParsed = parseFloat(salePrice);
        if (Number.isNaN(salePriceNumParsed) || salePriceNumParsed <= 0) {
          throw new Error('Invalid sale price. Please enter a valid positive number.');
        }
        const built = buildInventoryPricingFields({}, salePriceNumParsed);
        salePriceNum = built.sale_price;
        type_prices = built.type_prices;
      }
      
      if (!selectedSupermarketId || !selectedProductId) {
        throw new Error('Missing supermarket or product selection.');
      }
      
      if (!currentUser?.uid) {
        throw new Error('User authentication required.');
      }

      await addDoc(collection(db, 'Inventory'), {
        supermarket_id: selectedSupermarketId,
        supermarket_name: supermarket.display_name || 'Unknown hardware store', 
        product_id: selectedProductId,
        product_name: product.display_name || 'Unknown Product', 
        sale_price: salePriceNum,
        type_prices,
        quantity: quantityNum,
        ...(productHasTypes
          ? {
              type_quantities,
              type_discount_deals,
              discount_deals: [],
            }
          : {
              discount_deals: discountDeals.length > 0 ? discountDeals.map(deal => ({
                  quantity: deal.quantity,
                  price: deal.price
                })) : [],
            }),
        created_by_uid: currentUser.uid,
        created_by_email: currentUser.email || 'Unknown User',
        created_at: serverTimestamp(),
      });

      const savedProductId = selectedProductId;
      const savedProductName = product.display_name;

      setSelectedProductId('');
      setSelectedProduct(null);
      setProductSearchTerm('');
      setSalePrice('');
      setQuantity('');
      setDiscountDeals([]);
      setTypeSelections({});
      setTypePriceInputs({});
      setTypeQuantityInputs({});
      setTypeDealsRecord({});
      setTypeDealDrafts({});
      
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Inventory item added successfully!'
      });
      
      const updatedInventory = [...existingInventory, {
        supermarket_id: selectedSupermarketId,
        product_id: savedProductId,
        product_name: savedProductName,
        sale_price: salePriceNum,
        type_prices,
        quantity: quantityNum,
        ...(productHasTypes ? { type_quantities, type_discount_deals } : {}),
      }];
      setExistingInventory(updatedInventory);

    } catch (err) {
      showNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to add product to inventory. ${err.message}`
      });
      logger.error(err);
    }
    setLoading(false);
  };

  const handleGoBack = () => navigate(-1);

  if (pageLoading && !currentUser) {
    return (
      <Layout>
        <Container className="px-3">
          <p>Loading user data...</p>
        </Container>
      </Layout>
    );
  }
  if (userRole === 'Employee' && !employeeSupermarketId && !pageLoading) {
      return (
          <Layout>
              <Container className="mt-3 px-3">
                  <Button variant="outline-secondary" onClick={handleGoBack} className="mb-3">&larr; Go Back</Button>
                  <Alert variant="danger">You are not assigned to any supermarket. Please contact an administrator to add inventory.</Alert>
              </Container>
          </Layout>
      );
  }

  return (
    <Layout>
      <Container className="px-3">
         <Button variant="outline-secondary" onClick={handleGoBack} className="mt-3 mb-3">
          &larr; Go Back
        </Button>
        <h1 className="mb-3 text-break">Add Item to Inventory</h1>
        {error && <Alert variant="danger">{error}</Alert>} 

        {pageLoading ? (
          <div className="text-center">
            <Spinner animation="border" className="mb-3" />
            <p>Loading form data...</p>
          </div>
        ) : (
            <Form onSubmit={handleSubmit}>
            <Form.Group controlId="supermarketSelect" className="mb-3">
                <Form.Label>Select Supermarket</Form.Label>
                <Form.Control
                as="select"
                value={selectedSupermarketId}
                onChange={(e) => setSelectedSupermarketId(e.target.value)}
                required
                disabled={isEmployeeLimited}
                className="w-100"
                >
                {userRole === 'Admin' && <option value="">Select Supermarket</option>}
                {supermarkets.map((supermarket) => (
                    <option key={supermarket.id} value={supermarket.id}>
                    {supermarket.display_name}
                    </option>
                ))}
                {isEmployeeLimited && supermarkets.length === 0 && <option value="">Your assigned store data is unavailable</option>}
                </Form.Control>
            </Form.Group>

            <Form.Group controlId="productSelect" className="mb-3">
                <Form.Label>Select Product</Form.Label>
                {selectedSupermarketId && existingInventory.length > 0 && (
                    <div className="mb-2">
                        <small className="text-muted">
                            {existingInventory.length} product{existingInventory.length !== 1 ? 's' : ''} already in inventory. 
                            Products already added will be marked with a warning badge.
                        </small>
                    </div>
                )}
                <div className="position-relative product-search-container">
                    <div className="position-relative">
                        <Form.Control
                            type="text"
                            placeholder="Search for a product..."
                            value={productSearchTerm}
                            onChange={handleProductSearchChange}
                            onFocus={() => setShowProductDropdown(true)}
                            required
                            className="w-100"
                            style={{ fontSize: '16px' }} // Prevents zoom on iOS
                        />
                        {selectedProduct && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setSelectedProduct(null);
                                    setSelectedProductId('');
                                    setProductSearchTerm('');
                                }}
                                style={{ 
                                    fontSize: '16px', 
                                    minWidth: '44px',
                                    position: 'absolute',
                                    right: '0',
                                    top: '0',
                                    height: '100%',
                                    borderTopLeftRadius: '0',
                                    borderBottomLeftRadius: '0',
                                    zIndex: 10
                                }}
                            >
                                ×
                            </Button>
                        )}
                    </div>
                   
                    {showProductDropdown && (
                        <div 
                            className="position-absolute w-100 bg-white border rounded-bottom shadow-sm"
                            style={{ 
                                zIndex: 1000, 
                                maxHeight: '300px', 
                                overflowY: 'auto',
                                top: '100%',
                                maxWidth: '100vw'
                            }}
                        >
                            {filteredProducts.length > 0 ? (
                                <ListGroup variant="flush">
                                    {filteredProducts.map((product) => {
                                        const isAlreadyInInventory = checkIfProductAlreadyInInventory(product.id);
                                        return (
                                            <ListGroup.Item
                                                key={product.id}
                                                action
                                                onClick={() => handleProductSelect(product)}
                                                className="cursor-pointer p-3"
                                                style={{ cursor: 'pointer' }}
                                                disabled={isAlreadyInInventory}
                                            >
                                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                                                    <div className="flex-grow-1 w-100">
                                                        <div className="fw-semibold text-break fs-6">
                                                            {product.display_name}
                                                        </div>
                                                        {product.description && (
                                                            <div className="small text-muted text-break mt-1">
                                                                {product.description.length > 50 
                                                                    ? `${product.description.substring(0, 50)}...` 
                                                                    : product.description
                                                                }
                                                            </div>
                                                        )}
                                                        {isAlreadyInInventory && (
                                                            <div className="small text-warning mt-2">
                                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                                Already in inventory - use edit option
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-wrap align-items-start gap-1">
                                                        {product.category && (
                                                            <Badge bg="secondary" className="flex-shrink-0 small">
                                                                {product.category}
                                                            </Badge>
                                                        )}
                                                        {isAlreadyInInventory && (
                                                            <Badge bg="warning" text="dark" className="flex-shrink-0 small">
                                                                Added
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            ) : (
                                <div className="p-4 text-center text-muted">
                                    <div className="small mb-2">
                                        {productSearchTerm.trim() 
                                            ? 'No products found' 
                                            : 'Start typing to search products'
                                        }
                                    </div>
                                </div>
                            )}
                            {selectedProduct && (
                                <div className="border-top p-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedProduct(null);
                                            setSelectedProductId('');
                                            setProductSearchTerm('');
                                            setShowProductDropdown(false);
                                        }}
                                        className="w-100"
                                    >
                                        Cancel Selection
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {selectedProduct && (
                    <div className="mt-2 p-3 bg-light rounded border">
                        <div className="d-flex flex-column gap-2">
                            <div>
                                <small className="text-muted">Selected: </small>
                                <span className="fw-semibold text-break">{selectedProduct.display_name}</span>
                            </div>
                            {selectedProduct.description && (
                                <div className="small text-muted text-break">
                                    {selectedProduct.description}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Form.Group>

            {!productHasTypes ? (
              <Form.Group controlId="salePrice" className="mb-3">
                <Form.Label>Sale Price</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  placeholder="Enter sale price" 
                  value={salePrice} 
                  onChange={(e) => setSalePrice(e.target.value)} 
                  required 
                  className="w-100"
                  style={{ fontSize: '16px' }}
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Prices by product type</Form.Label>
                <small className="text-muted d-block mb-2">
                  Check each type you stock. Set unit price, quantity on hand, and optional bulk deals for that type only.
                </small>
                {selectedProduct.product_types.map((t) => (
                  <div key={t.id} className="border rounded p-2 mb-3">
                    <Form.Check
                      type="checkbox"
                      id={`inv-type-${t.id}`}
                      label={<span className="fw-semibold">{t.name || t.id}</span>}
                      checked={!!typeSelections[t.id]}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setTypeSelections((prev) => ({ ...prev, [t.id]: on }));
                        if (!on) {
                          setTypePriceInputs((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeQuantityInputs((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeDealsRecord((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeDealDrafts((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                        }
                      }}
                    />
                    {typeSelections[t.id] && (
                      <div className="mt-2 ps-1">
                        <Row className="g-2 mb-2">
                          <Col xs={12} sm={6}>
                            <Form.Label className="small mb-1">Unit price</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={typePriceInputs[t.id] || ''}
                              onChange={(e) => setTypePriceInputs((prev) => ({ ...prev, [t.id]: e.target.value }))}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Label className="small mb-1">Quantity in stock</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              step="1"
                              placeholder="0"
                              value={typeQuantityInputs[t.id] ?? ''}
                              onChange={(e) => setTypeQuantityInputs((prev) => ({ ...prev, [t.id]: e.target.value }))}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                        </Row>
                        <div className="small text-muted mb-1">Bulk deals for this type (optional)</div>
                        <Row className="g-2 align-items-end mb-2">
                          <Col xs={6} md={4}>
                            <Form.Control
                              type="number"
                              step="1"
                              placeholder="Deal qty"
                              value={getTypeDealDraft(t.id).q}
                              onChange={(e) => setTypeDealDraftField(t.id, 'q', e.target.value)}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={6} md={4}>
                            <Form.Control
                              type="number"
                              step="0.01"
                              placeholder="Deal price"
                              value={getTypeDealDraft(t.id).p}
                              onChange={(e) => setTypeDealDraftField(t.id, 'p', e.target.value)}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={12} md={4}>
                            <Button type="button" variant="outline-primary" size="sm" className="w-100" onClick={() => addDiscountDealForType(t.id)}>
                              Add deal
                            </Button>
                          </Col>
                        </Row>
                        {(typeDealsRecord[t.id] || []).length > 0 && (
                          <ListGroup variant="flush" className="small">
                            {(typeDealsRecord[t.id] || []).map((deal) => {
                              const unit = unitPriceForType(t.id);
                              const regularPrice = (unit != null ? unit : 0) * deal.quantity;
                              const savings = regularPrice - deal.price;
                              return (
                                <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center px-0 py-2">
                                  <span>
                                    <strong>{deal.quantity}</strong> for <strong>${deal.price.toFixed(2)}</strong>
                                    <span className="text-success ms-1">(save ${savings.toFixed(2)})</span>
                                  </span>
                                  <Button variant="outline-danger" size="sm" onClick={() => removeDiscountDealForType(t.id, deal.id)}>
                                    Remove
                                  </Button>
                                </ListGroup.Item>
                              );
                            })}
                          </ListGroup>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </Form.Group>
            )}

            {!productHasTypes && (
            <Form.Group controlId="quantity" className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control 
                  type="number" 
                  step="1" 
                  placeholder="Enter quantity" 
                  value={quantity} 
                  onChange={(e) => setQuantity(e.target.value)} 
                  required 
                  className="w-100"
                  style={{ fontSize: '16px' }}
                />
            </Form.Group>
            )}

            {!productHasTypes && (
            <Form.Group controlId="discountDeals" className="mb-3">
                <Form.Label>Discount Deals (Optional)</Form.Label>
                <small className="text-muted d-block mb-2">
                  Create bulk purchase discounts. For example: Buy 3 items for $8.00 instead of $12.00
                </small>
                <Card className="mb-3">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    placeholder="Quantity for deal"
                                    value={dealQuantity}
                                    onChange={(e) => setDealQuantity(e.target.value)}
                                    className="w-100"
                                    style={{ fontSize: '16px' }}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="Price for deal"
                                    value={dealPrice}
                                    onChange={(e) => setDealPrice(e.target.value)}
                                    className="w-100"
                                    style={{ fontSize: '16px' }}
                                />
                            </Col>
                        </Row>
                        <Button
                            variant="outline-primary"
                            onClick={addDiscountDeal}
                            className="mt-2"
                            disabled={!dealQuantity || !dealPrice}
                        >
                            Add Deal
                        </Button>
                    </Card.Body>
                </Card>

                {discountDeals.length > 0 && (
                    <div className="mb-3">
                        <h6>Current Discount Deals:</h6>
                        <ListGroup>
                            {discountDeals.map((deal) => {
                                const unit = getFormBaselineUnitPrice();
                                const regularPrice = (unit != null ? unit : 0) * deal.quantity;
                                const savings = regularPrice - deal.price;
                                return (
                                    <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                                            <br />
                                            <small className="text-success">
                                                Save ${savings.toFixed(2)} (${(deal.price / deal.quantity).toFixed(2)} per item)
                                            </small>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeDiscountDeal(deal.id)}
                                        >
                                            Remove
                                        </Button>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </div>
                )}
            </Form.Group>
            )}

            <Button 
              type="submit" 
              variant="primary" 
              className="w-100 mt-4 py-3"
              disabled={loading || (userRole === 'Employee' && !employeeSupermarketId)}
              style={{ fontSize: '16px' }}
            >
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </div>
                ) : (
                  'Add to Inventory'
                )}
            </Button>
            </Form>
        )}
      </Container>
    </Layout>
  );
};

export default CreateInventory;
